import { useEffect, useState } from "react";
import { api, eur, dateFr } from "@/lib/rentApi";
import { useAuth } from "@/context/AuthContext";
import { StatCard, Loading } from "@/components/Shared";
import {
  Building2, FileText, Wrench, Users, Wallet, TrendingUp, TrendingDown, Calculator, CheckCircle2, ClockAlert,
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data)).catch(() => setData({}));
  }, []);

  if (!data) return <Loading />;

  return (
    <div>
      <div className="mb-6">
        <h1 className="font-heading text-2xl lg:text-3xl font-bold">Bonjour, {user?.name?.split(" ")[0]} 👋</h1>
        <p className="text-sm text-muted-foreground mt-1">Voici l'essentiel de votre activité.</p>
      </div>

      {user.role === "proprietaire" || user.role === "admin" ? (
        <OwnerView data={data} />
      ) : user.role === "locataire" ? (
        <TenantView data={data} />
      ) : user.role === "candidat" ? (
        <CandidateView data={data} />
      ) : (
        <MaintainerView data={data} />
      )}
    </div>
  );
}

function OwnerView({ data }) {
  return (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Biens" value={data.properties} icon={Building2} testid="stat-properties" />
        <StatCard label="Baux actifs" value={data.active_leases} icon={FileText} testid="stat-leases" />
        <StatCard label="Candidatures" value={data.pending_applications} icon={Users} testid="stat-applications" />
        <StatCard label="Tickets ouverts" value={data.open_tickets} icon={Wrench} testid="stat-tickets" />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
        <StatCard label="Loyers encaissés" value={eur(data.collected_rent)} icon={Wallet} accent="text-[hsl(var(--chart-2))]" testid="stat-collected" />
        <StatCard label="Loyers en attente" value={eur(data.pending_rent)} icon={ClockAlert} accent="text-[hsl(var(--chart-3))]" testid="stat-pending-rent" />
        <StatCard label="Recettes (année)" value={eur(data.revenues)} icon={TrendingUp} accent="text-[hsl(var(--chart-2))]" />
        <StatCard label="Charges (année)" value={eur(data.expenses)} icon={TrendingDown} accent="text-destructive" />
      </div>

      <div className="grid lg:grid-cols-3 gap-4 mt-6">
        <div className="lg:col-span-2 p-5 rounded-xl border border-border bg-card">
          <h3 className="font-heading font-semibold mb-4">Recettes vs charges ({new Date().getFullYear()})</h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={data.monthly}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
              <XAxis dataKey="mois" fontSize={12} stroke="hsl(var(--muted-foreground))" />
              <YAxis fontSize={12} stroke="hsl(var(--muted-foreground))" />
              <Tooltip formatter={(v) => eur(v)} />
              <Legend />
              <Bar dataKey="recettes" name="Recettes" fill="hsl(var(--chart-2))" radius={[4, 4, 0, 0]} />
              <Bar dataKey="charges" name="Charges" fill="hsl(var(--chart-4))" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="p-5 rounded-xl border border-border bg-card flex flex-col justify-center">
          <div className="flex items-center gap-2 text-muted-foreground text-sm"><Calculator className="w-4 h-4" /> Résultat fiscal estimé</div>
          <p className={`font-heading text-4xl font-bold mt-3 ${data.result >= 0 ? "" : "text-destructive"}`} data-testid="stat-result">{eur(data.result)}</p>
          <p className="text-xs text-muted-foreground mt-2">Recettes − charges − amortissements ({eur(data.amortization)})</p>
        </div>
      </div>
    </>
  );
}

function TenantView({ data }) {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      <StatCard label="Mon bail" value={data.leases} icon={FileText} testid="stat-leases" />
      <StatCard label="Loyers dus" value={eur(data.unpaid)} icon={Wallet} accent="text-[hsl(var(--chart-3))]" testid="stat-unpaid" />
      <StatCard label="Prochaine échéance" value={dateFr(data.next_due)} icon={ClockAlert} />
      <StatCard label="Mes tickets" value={data.tickets} icon={Wrench} testid="stat-tickets" />
    </div>
  );
}

function CandidateView({ data }) {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      <StatCard label="Mes candidatures" value={data.applications} icon={Users} testid="stat-applications" />
      <StatCard label="En attente" value={data.pending} icon={ClockAlert} accent="text-[hsl(var(--chart-3))]" />
      <StatCard label="Acceptées" value={data.accepted} icon={CheckCircle2} accent="text-[hsl(var(--chart-2))]" />
      <StatCard label="Annonces dispo." value={data.available_properties} icon={Building2} />
    </div>
  );
}

function MaintainerView({ data }) {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
      <StatCard label="Qui me sont affectés" value={data.assigned} icon={Wrench} testid="stat-assigned" />
      <StatCard label="Non affectés" value={data.unassigned} icon={ClockAlert} accent="text-[hsl(var(--chart-3))]" />
      <StatCard label="Résolus" value={data.resolved} icon={CheckCircle2} accent="text-[hsl(var(--chart-2))]" />
    </div>
  );
}
